import * as t from 'io-ts';

import { tPhoto } from './photo';

export const tSummarizedBrandSpecialPrices = t.partial({
  price_id: t.string,
  discounted_price: t.number,
});

export const tSummarizedBrandSpecial = t.partial({
  discount_label: t.string,
  id: t.number,
  special_type: t.string,
  title: t.string,
  prices: t.union([tSummarizedBrandSpecialPrices, t.undefined, t.null]),
});

export const tSummarizedBrandSpecials = t.union([
  t.array(tSummarizedBrandSpecial),
  t.undefined,
  t.null,
]);

export type SummarizedBrandSpecials = t.TypeOf<typeof tSummarizedBrandSpecials>;

export const tBrandSpecialUserSegmentIds = t.union([
  t.array(t.number),
  t.number,
  t.null,
]);

export type BrandSpecialUserSegmentIds = t.TypeOf<
  typeof tBrandSpecialUserSegmentIds
>;

export const tBrandSpecialType = t.union([
  t.literal('product'),
  t.literal('buy_x_get_y'),
  t.literal('buy_x_and_save'),
]);

const tBundleApplicableWeights = t.partial({
  qualify: t.array(t.string),
  discount: t.array(t.string),
});

const tProductBrand = t.type({
  id: t.union([t.number, t.string]),
  name: t.string,
  logo_url: t.union([t.string, t.null]),
});

const tBrandSpecialInclusionRules = t.array(
  t.partial({
    kinds: t.array(t.string),
    lineages: t.array(t.string),
    product_ids: t.union([t.array(t.number), t.null]),
    states: t.union([t.array(t.string), t.null]),
    store_ids: t.union([t.array(t.string), t.null]),
    user_segment_ids: tBrandSpecialUserSegmentIds,
    weights: t.array(t.string),
  })
);

const tBrandSpecialExclusionRules = t.array(
  t.partial({
    kinds: t.array(t.string),
    lineages: t.array(t.string),
    product_ids: t.union([t.array(t.number), t.null]),
    states: t.union([t.array(t.string), t.null]),
    store_ids: t.union([t.array(t.string), t.null]),
    user_segment_ids: tBrandSpecialUserSegmentIds,
    weights: t.array(t.string),
  })
);

const tBrandSpecialRuleSection = t.partial({
  includes: tBrandSpecialInclusionRules,
  excludes: tBrandSpecialExclusionRules,
});

const tBundleSettings = t.partial({
  threshold_number_of_items_in_cart: t.union([t.number, t.null]),
  max_number_of_discounted_products: t.union([t.number, t.null]),
  discount_application_method: t.union([t.string, t.null]),
  discount_distribution: t.union([t.string, t.null]),
});

// For the base rule section (which is not an array)
const tBundleBaseSection = tBrandSpecialRuleSection;

// For qualifier and discountable sections (which are arrays)
const tBundleItemSection = t.array(tBrandSpecialRuleSection);

const tBrandSpecialRules = t.partial({
  // Product type rules
  excludes: tBrandSpecialExclusionRules,
  includes: tBrandSpecialInclusionRules,

  // Bundle type rules
  settings: tBundleSettings,
  base: tBundleBaseSection,
  qualifier: tBundleItemSection,
  discountable: tBundleItemSection,
});

export const tBrandSpecial = t.type({
  active: t.boolean,
  applicable_weights: t.union([t.array(t.string), tBundleApplicableWeights]),
  created_at: t.string,
  description: t.string,
  discount_dollar_amount: t.number,
  discount_label: t.string,
  discount_percent: t.number,
  discount_target_price: t.number,
  discount_type: t.string,
  enabled: t.boolean,
  end_time: t.union([t.string, t.null]),
  excluded_app_modes: t.union([t.array(t.string), t.null]),
  id: t.number,
  photo: t.union([tPhoto, t.null]),
  product_brand: tProductBrand,
  product_brand_id: t.number,
  rules: tBrandSpecialRules,
  special_type: tBrandSpecialType,
  start_time: t.union([t.string, t.null]),
  terms: t.union([t.string, t.null]),
  title: t.string,
  updated_at: t.string,
});

export type BrandSpecial = t.TypeOf<typeof tBrandSpecial>;
